import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Button, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Checkbox, Chip, Alert, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Stack,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  listMembers, addMembers, removeMember, retryMember, reconcileAssociations, getSecurityHubConfig,
  listSecurityHubAccounts, getConfigStatus,
} from '../../../services/securityhubApi';
import { missingEnableCells } from './configStatus';
import ConfigSetupDialog from './ConfigSetupDialog';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalEmptyState } from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { formatStatusLabel } from '../../../utils/statusLabel';
import {
  CK, FONT_BODY, statusChipSx, intentFromColor, tableHeadRowSx, headCellSx, rowSx, bodyCellSx,
  sectionCardSx, listCardSx, sectionHeadingSx, splitRowSx, actionRowSx,
  dialogProps, dialogTitleSx,
} from '../portalStyles';

const enrollmentHeader = (
  <PortalPageHeader
    icon={<GroupsIcon sx={{ fontSize: 24 }} />}
    title="Enrollment"
    subtitle="Manage which member accounts are enrolled in Security Hub"
  />
);

const alertSx = {
  borderRadius: '8px',
  fontFamily: FONT_BODY,
  '& .MuiAlert-message': { fontFamily: FONT_BODY },
};

// SummaryField renders one label/value pair of the deployment summary card.
function SummaryField({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.75rem',
          fontWeight: 600,
          color: CK.muted,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT_BODY,
          fontSize: '0.875rem',
          fontWeight: 600,
          color: CK.navy,
          wordBreak: 'break-word',
        }}
      >
        {value || '—'}
      </Typography>
    </Box>
  );
}

const rowActionSx = (fg, bg) => ({
  width: 96,
  whiteSpace: 'nowrap',
  color: fg,
  borderColor: `${fg}40`,
  transition: 'background-color 0.2s ease, border-color 0.2s ease',
  '&:hover': { borderColor: fg, backgroundColor: bg },
});

const phaseColor = (p) => ({
  enabled: 'success',
  failed: 'error',
  pending: 'default',
  enabling: 'info',
  inviting: 'info',
  accepting: 'info',
  associating_standards: 'info',
  removing: 'warning',
  disabled: 'default',
}[p] || 'default');

const activePhases = ['pending', 'enabling', 'inviting', 'accepting', 'associating_standards', 'removing'];

export default function EnrollmentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [members, setMembers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [configCells, setConfigCells] = useState([]);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    let cfgData = null;
    try {
      // Config + members determine whether Security Hub is set up; these must
      // succeed for the page to render the enrollment status.
      const [c, m] = await Promise.all([getSecurityHubConfig(), listMembers()]);
      cfgData = c?.data || null;
      setConfig(cfgData);
      setMembers(m?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
    // The candidate-account list is best-effort: never let it blank the page
    // (a failure here must not be mistaken for "Security Hub not set up").
    try {
      const a = await listSecurityHubAccounts();
      setAccounts(a?.data?.accounts || a?.data || []);
    } catch (_) {
      setAccounts([]);
    }
    // Best-effort: which enrolled account/region pairs still need AWS Config, so
    // the user can enable them inline (one click) without leaving the page.
    try {
      const cs = await getConfigStatus();
      let linking = [];
      try { linking = JSON.parse(cfgData?.linkingRegions || '[]'); } catch (_) { linking = []; }
      setConfigCells(missingEnableCells(cs?.data?.recorders || [], linking));
    } catch (_) {
      setConfigCells([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Start a poll whenever any member enters an active phase.
  // IMPORTANT: do NOT put clearInterval in this effect's cleanup. The interval
  // callback calls setMembers(next) on every tick; that produces a new array
  // reference each time, so [members] changes and React fires the cleanup —
  // killing the interval and restarting its 3-second countdown on every tick.
  // That destroy-and-recreate cycle prevents config.progress from advancing in
  // the rendered bar. Instead the interval stops itself when no active phases
  // remain (inner check below), and a separate unmount effect handles cleanup.
  useEffect(() => {
    // Poll while EITHER a member cell is active OR the config is mid-operation.
    // The config check is essential for ops (like reconcile) whose first phase is
    // a server-side pre-check: at trigger time no member is active yet, so keying
    // only on member phases would never start the poll and the bar/status would
    // sit frozen for the whole operation.
    const active = config?.status === 'in_progress' || members.some((m) => activePhases.includes(m.phase));
    if (active && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          // Refresh config too so the operation progress bar (config.progress)
          // advances live, not just the per-cell member phases.
          const [m, c] = await Promise.all([listMembers(), getSecurityHubConfig()]);
          const next = m?.data || [];
          const nextCfg = c?.data || null;
          setMembers(next);
          setConfig(nextCfg);
          const stillActive = nextCfg?.status === 'in_progress' || next.some((r) => activePhases.includes(r.phase));
          if (!stillActive && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch (_) { /* transient */ }
      }, POLL_INTERVAL_MS);
    }
  }, [members, config?.status]);

  // Stop the interval when the component unmounts.
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, []);

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading enrolled accounts…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert severity="info" sx={alertSx} action={
            <Button color="inherit" size="small" onClick={() => navigate('/securityhub/cspm')}>
              Go to setup
            </Button>
          }>
            Security Hub is not set up yet for this customer. Complete the setup wizard first to enroll member accounts.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const worstPhase = (phases) => {
    if (phases.includes('failed')) return 'failed';
    if (phases.includes('pending')) return 'pending';
    const active = ['enabling', 'inviting', 'accepting', 'associating_standards', 'removing'];
    for (const p of active) if (phases.includes(p)) return p;
    if (phases.every((p) => p === 'enabled')) return 'enabled';
    if (phases.every((p) => p === 'disabled')) return 'disabled';
    return phases[0];
  };

  const byAccount = new Map();
  for (const m of members) {
    const prev = byAccount.get(m.accountId) || {
      accountId: m.accountId, phases: [], errors: [],
    };
    prev.phases.push(m.phase);
    if (m.errorMessage) prev.errors.push(`${m.region}: ${m.errorMessage}`);
    byAccount.set(m.accountId, prev);
  }
  // A fully-disabled (previously-removed) member is treated as NOT enrolled: it
  // drops out of the enrolled list and reappears under "Not enrolled" so it can
  // be re-enrolled through the normal Add flow.
  const enrolledAccounts = [...byAccount.values()].filter(
    (row) => worstPhase(row.phases) !== 'disabled',
  );
  const enrolledIds = new Set(enrolledAccounts.map((r) => r.accountId));
  const notEnrolled = accounts.filter((a) => !enrolledIds.has(a.accountId));

  const nameById = {};
  accounts.forEach((a) => { if (a.accountId) nameById[a.accountId] = a.name || a.accountName || ''; });
  const acctLabel = (id) => (nameById[id] ? `${nameById[id]} (${id})` : id);

  const onAdd = async () => {
    setBusy(true);
    try {
      await addMembers([...selected]);
      setSelected(new Set());
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async (accountId) => {
    setBusy(true);
    try {
      await removeMember(accountId);
      setConfirmRemove(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onRetry = async (accountId) => {
    setBusy(true);
    try {
      await retryMember(accountId);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onReconcile = async () => {
    setBusy(true);
    try {
      await reconcileAssociations();
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const linkingRegionsDisplay = (() => {
    try { return JSON.parse(config.linkingRegions || '[]').join(', '); } catch (_) { return ''; }
  })();

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{enrollmentHeader}</motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ ...alertSx, mb: 2 }}>{error}</Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SummaryField label="Admin account" value={config.adminAccountId} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SummaryField label="Home region" value={config.homeRegion} />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <SummaryField label="Linking regions" value={linkingRegionsDisplay} />
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {configCells.length > 0 && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="warning"
            sx={{ ...alertSx, mb: 3 }}
            action={<Button color="inherit" size="small" onClick={() => setConfigDialogOpen(true)}>Set up AWS Config</Button>}
          >
            AWS Config isn&apos;t recording in {configCells.length} enrolled account/region pair(s). The standards can&apos;t evaluate those accounts until it&apos;s set up.
          </Alert>
        </motion.div>
      )}

      {(config?.status === 'in_progress' || members.some((m) => activePhases.includes(m.phase))) && (
        <motion.div variants={sectionVariants}>
          <OperationProgress percent={config.progress} label={config.progressLabel || 'Working…'} sx={{ mb: 3 }} />
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...listCardSx, mb: 3 }}>
          <Box sx={{ ...splitRowSx, px: 3, pt: 3, pb: 2 }}>
            <Typography sx={{ ...sectionHeadingSx, minWidth: 0 }}>Enrolled accounts</Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={busy}
              onClick={onReconcile}
              title="Associate members with the admin in every linking region — fixes accounts whose findings only show in the home region"
            >
              Repair region associations
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx}>Account</TableCell>
                  <TableCell sx={headCellSx}>Status</TableCell>
                  <TableCell sx={headCellSx} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enrolledAccounts.map((row, i) => {
                  const phase = worstPhase(row.phases);
                  const isAdmin = row.accountId === config.adminAccountId;
                  return (
                    <TableRow key={row.accountId} hover sx={rowSx(i)}>
                      <TableCell sx={bodyCellSx}>
                        {acctLabel(row.accountId)}
                        {isAdmin && <Chip label="admin" size="small" sx={{ ...statusChipSx('info'), ml: 1 }} />}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip label={formatStatusLabel(phase)} size="small" sx={statusChipSx(intentFromColor(phaseColor(phase)))} />
                          {activePhases.includes(phase) && <CircularProgress size={14} thickness={5} sx={{ color: CK.blue }} />}
                        </Stack>
                        {row.errors.length > 0 && (
                          <Typography
                            sx={{
                              fontFamily: FONT_BODY,
                              fontSize: '0.75rem',
                              color: CK.red,
                              display: 'block',
                              mt: 0.5,
                              wordBreak: 'break-word',
                            }}
                          >
                            {row.errors.join(' • ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={bodyCellSx} align="right">
                        <Box sx={{ ...actionRowSx, gap: 1, justifyContent: 'flex-end' }}>
                          {!isAdmin && phase === 'failed' && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => onRetry(row.accountId)}
                              sx={rowActionSx(CK.amber, '#FEF9E7')}
                            >
                              Retry
                            </Button>
                          )}
                          {!isAdmin && phase !== 'disabled' && phase !== 'removing' && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setConfirmRemove(row.accountId)}
                              sx={rowActionSx(CK.red, '#FDF2F2')}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {enrolledAccounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<GroupsIcon sx={{ fontSize: 28 }} />}
                        title="No accounts enrolled yet"
                        description="Pick accounts from the Not enrolled list below and add them to Security Hub."
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </motion.div>

      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <Box sx={{ ...splitRowSx, px: 3, pt: 3, pb: 2 }}>
            <Typography sx={{ ...sectionHeadingSx, minWidth: 0 }}>Not enrolled</Typography>
            <Button variant="contained" disabled={busy || selected.size === 0} onClick={onAdd}>
              Add {selected.size} to Security Hub
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  <TableCell sx={headCellSx} padding="checkbox">
                    <Checkbox
                      indeterminate={notEnrolled.some((a) => selected.has(a.accountId)) && !notEnrolled.every((a) => selected.has(a.accountId))}
                      checked={notEnrolled.length > 0 && notEnrolled.every((a) => selected.has(a.accountId))}
                      disabled={notEnrolled.length === 0}
                      onChange={(e) => setSelected(e.target.checked ? new Set(notEnrolled.map((a) => a.accountId)) : new Set())}
                    />
                  </TableCell>
                  <TableCell sx={headCellSx}>Account</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notEnrolled.map((a, i) => (
                  <TableRow key={a.accountId} hover sx={rowSx(i)}>
                    <TableCell sx={bodyCellSx} padding="checkbox">
                      <Checkbox
                        checked={selected.has(a.accountId)}
                        onChange={(e) => {
                          const n = new Set(selected);
                          if (e.target.checked) n.add(a.accountId);
                          else n.delete(a.accountId);
                          setSelected(n);
                        }}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx}>{a.accountName || a.name || a.accountId} ({a.accountId})</TableCell>
                  </TableRow>
                ))}
                {notEnrolled.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <PortalEmptyState
                        icon={<GroupsIcon sx={{ fontSize: 28 }} />}
                        title="Everything is enrolled"
                        description="All linked accounts are already enrolled in Security Hub."
                        py={2}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </motion.div>

      <Dialog open={!!confirmRemove} onClose={() => setConfirmRemove(null)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Remove account from Security Hub</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
            Remove <b style={{ color: CK.navy }}>{confirmRemove}</b>? Its Security Hub data in that
            account will be disabled.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => onRemove(confirmRemove)} disabled={busy}>Remove</Button>
        </DialogActions>
      </Dialog>

      <ConfigSetupDialog
        open={configDialogOpen}
        mode="enable"
        cells={configCells}
        onClose={() => setConfigDialogOpen(false)}
        onDone={() => { setConfigDialogOpen(false); load(); }}
      />
    </motion.div>
  );
}
